import React, { useState, useEffect, useMemo, useContext, createContext } from "react";
import {
  getAuth,
  onAuthStateChanged,
  signOut as authSignOut,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  FacebookAuthProvider,
  TwitterAuthProvider,
  GithubAuthProvider,
  sendEmailVerification,
  checkActionCode,
  applyActionCode,
  getAdditionalUserInfo,
  updateEmail as authUpdateEmail,
  updateProfile as authUpdateProfile,
  updatePassword as authUpdatePassword,
  sendPasswordResetEmail as authSendPasswordResetEmail,
  confirmPasswordReset as authConfirmPasswordReset,
} from "firebase/auth";
import { firebaseApp } from "./firebase";
import { useUser, createUser, updateUser, watchUserDoc } from "./db";
import { history } from "./router";
import PageLoader from "../components/PageLoader";
import { getFriendlyPlanId } from "./prices";
import analytics from "./analytics";

// Config flags
const MERGE_DB_USER = true;
const EMAIL_VERIFICATION = true;
const ANALYTICS_IDENTIFY = true;

// Initialize Firebase auth
const auth = getAuth(firebaseApp);

// Context setup
const authContext = createContext();
export const useAuth = () => useContext(authContext);

export function AuthProvider({ children }) {
  const authValue = useAuthProvider();
  return <authContext.Provider value={authValue}>{children}</authContext.Provider>;
}

function useAuthProvider() {
  const [isAuthInitializing, setIsAuthInitializing] = useState(true);
  const [rawUser, setRawUser] = useState(null); // Firebase user object
  const [user, setUser] = useState(null); // merged finalUser for UI
  const [authError, setAuthError] = useState(null);

  // Merge Firestore data into UI user
  let finalUser = useMergeExtraData(rawUser, { enabled: MERGE_DB_USER });
  const isDbUserReady = rawUser && finalUser !== null;

  // Set initializing = false only after both auth and Firestore merge are ready
// useEffect(() => {
//   // If user is null (logged out) OR Firestore merge is done, stop initializing
//   if (rawUser === null || (rawUser !== null && (finalUser !== null || !MERGE_DB_USER))) {
//     setIsAuthInitializing(false);
//   }
// }, [rawUser, finalUser]);

useEffect(() => {
  if (rawUser !== null && (finalUser !== null || !MERGE_DB_USER)) {
    setIsAuthInitializing(false); // only now
  }
}, [rawUser, finalUser]);

  // Format user fields for UI
  finalUser = useFormatUser(finalUser);

  // Connect analytics session
  useIdentifyUser(finalUser, { enabled: ANALYTICS_IDENTIFY });

  // Handle auth responses (signup/signin)
  const handleAuth = async (response) => {
    const firebaseUser = response.user;
    if (!firebaseUser) throw new Error("No user returned from auth response");
    const { isNewUser } = getAdditionalUserInfo(response);

    if (isNewUser) {
      try {
        await createUser(firebaseUser.uid, { email: firebaseUser.email, userType: "production" });
        if (EMAIL_VERIFICATION) await sendEmailVerification(firebaseUser);
      } catch (err) {
        console.error("Error creating Firestore user:", err);
        throw err;
      }
    }

    return firebaseUser; // watcher will handle updating state
  };

  // Signup / Signin / SigninWithProvider
  const signup = async (email, password) => {
    const credential = await createUserWithEmailAndPassword(auth, email, password);
    return handleAuth(credential);
  };

  const signin = (email, password) => signInWithEmailAndPassword(auth, email, password).then(handleAuth);

  const signinWithProvider = (name) => {
    const provider = authProviders.find((p) => p.name === name).get();
    return signInWithPopup(auth, provider).then(handleAuth);
  };

const signout = async () => {
  await authSignOut(auth);
  setRawUser(null);       // ensure raw user is null
  setUser(null);          // clear UI user
  setIsAuthInitializing(false); // stop loader
};

  const sendPasswordResetEmail = (email) => authSendPasswordResetEmail(auth, email);

  const confirmPasswordReset = (password, code) => {
    const resetCode = code || getFromQueryString("oobCode");
    return authConfirmPasswordReset(auth, resetCode, password);
  };

  const updatePassword = (password) => authUpdatePassword(rawUser, password);

  const updateProfile = async (data) => {
    if (!rawUser) throw new Error("Firebase user not loaded");

    const { email, name, picture, ...extraData } = data;

    if (email) await authUpdateEmail(rawUser, email);

    if (name || picture) {
      const fields = {};
      if (name) fields.displayName = name;
      if (picture) fields.photoURL = picture;
      await authUpdateProfile(rawUser, fields);
    }

    // Save extra fields to database using rawUser.uid
    await updateUser(rawUser.uid, extraData);

    setRawUser(rawUser); // keep Firebase object
    setUser(finalUser);  // keep UI merged user
  };

  // Subscribe to Firebase auth changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          await firebaseUser.getIdToken(true);
          setRawUser(firebaseUser);
        } catch (err) {
          console.log("User deleted or token invalid:", err);
          setRawUser(null);
          authSignOut(auth);
        }
      } else {
        setRawUser(null);
      }
    });
    return () => unsubscribe();
  }, []);

  // Watch for deleted user
  useEffect(() => {
    if (!rawUser) return;

    const handleDeletedUser = () => {
      console.log("User deleted or deactivated");
      setRawUser(null);
      setUser(null);
      authSignOut(auth);
      setAuthError("User deleted or deactivated");
    };

    const unsubscribe = watchUserDoc(rawUser.uid, rawUser.metadata?.creationTime, handleDeletedUser);
    return () => unsubscribe();
  }, [rawUser]);

  return {
    user: finalUser,
    rawUser,
    authError,
    isAuthInitializing,
    signup,
    signin,
    signinWithProvider,
    signout,
    sendPasswordResetEmail,
    confirmPasswordReset,
    updatePassword,
    updateProfile,
  };
}

// Format user fields for UI
function useFormatUser(user) {
  return useMemo(() => {
    if (!user) return user;

    const providers = user.providerData?.map(({ providerId }) => {
      return authProviders.find((p) => p.id === providerId)?.name;
    }) || [];

    return {
      ...user,
      name: user.displayName,
      picture: user.photoURL,
      providers,
      ...(user.stripePriceId && { planId: getFriendlyPlanId(user.stripePriceId) }),
      planIsActive: ["active", "trialing"].includes(user.stripeSubscriptionStatus),
    };
  }, [user]);
}

// Merge Firestore data into user
function useMergeExtraData(user, { enabled }) {
  const { data, status, error } = useUser(enabled && user && user.uid);

  return useMemo(() => {
    if (!enabled || !user) return user;

    switch (status) {
      case "success":
        if (data === null) return null;
        return { ...user, ...data };
      case "error":
        throw new Error(`Error fetching extra user data: ${error.message}`);
      default:
        return null;
    }
  }, [user, enabled, data, status, error]);
}

// Connect analytics
function useIdentifyUser(user, { enabled }) {
  useEffect(() => {
    if (user && enabled) analytics.identify(user.uid);
  }, [user, enabled]);
}

// HOC for requiring auth
export const requireAuth = (Component) => {
  return function RequireAuthHOC(props) {
    const { rawUser, isAuthInitializing } = useAuth();

    if (isAuthInitializing || rawUser === null) return <PageLoader />;

    if (rawUser === false) return null; // redirect handled elsewhere

    return <Component {...props} />;
  };
};

// Handle email verification and recovery
export const handleRecoverEmail = async (code) => {
  const info = await checkActionCode(auth, code);
  await applyActionCode(auth, code);
  await authSendPasswordResetEmail(auth, info.data.email);
  return info.data.email;
};

export const handleVerifyEmail = (code) => applyActionCode(auth, code);

// Auth providers
const authProviders = [
  { id: "password", name: "password" },
  { id: "google.com", name: "google", get: () => new GoogleAuthProvider() },
  {
    id: "facebook.com",
    name: "facebook",
    get: () => {
      const provider = new FacebookAuthProvider();
      provider.setCustomParameters({ display: "popup" });
      return provider;
    },
  },
  { id: "twitter.com", name: "twitter", get: () => new TwitterAuthProvider() },
  { id: "github.com", name: "github", get: () => new GithubAuthProvider() },
];

// Helpers
const getFromQueryString = (key) => new URLSearchParams(window.location.search).get(key);
